import { ApiService } from "./api.service";
import type { TResolvedTemplateCustomField } from "@/types/project-template";

export interface ProjectCustomFieldValueResponse {
  custom_fields_schema: TResolvedTemplateCustomField[];
  custom_field_values: Record<string, unknown>;
}

/**
 * Service for the per-project custom-fields overlay (PRD §4-§6, §8).
 *
 * Endpoints are admin-gated server-side (``is_project_admin``). Each call
 * returns the project's resolved schema after the mutation so the caller can
 * refresh local state without a follow-up GET.
 */
export class ProjectCustomFieldsService extends ApiService {
  async addFields(
    slug: string,
    projectId: string,
    fieldIds: string[],
  ): Promise<TResolvedTemplateCustomField[]> {
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/custom-fields/`, {
      field_ids: fieldIds,
    });
  }

  async updateRequired(
    slug: string,
    projectId: string,
    fieldId: string,
    required: boolean,
  ): Promise<TResolvedTemplateCustomField[]> {
    return this.patch(`/api/workspaces/${slug}/projects/${projectId}/custom-fields/${fieldId}/`, {
      required,
    });
  }

  async removeField(
    slug: string,
    projectId: string,
    fieldId: string,
  ): Promise<TResolvedTemplateCustomField[]> {
    return this.delete(`/api/workspaces/${slug}/projects/${projectId}/custom-fields/${fieldId}/`);
  }

  async reorderFields(
    slug: string,
    projectId: string,
    orderedFieldIds: string[],
  ): Promise<TResolvedTemplateCustomField[]> {
    return this.patch(`/api/workspaces/${slug}/projects/${projectId}/custom-fields/`, {
      order: orderedFieldIds,
    });
  }

  /**
   * Write a single field's value on the project (admin-only). ``null``
   * clears the value. The BE returns the resolved schema *and* the updated
   * ``custom_field_values`` dict so callers can refresh state in one shot.
   */
  async setValue(
    slug: string,
    projectId: string,
    fieldId: string,
    value: unknown,
  ): Promise<ProjectCustomFieldValueResponse> {
    return this.patch(
      `/api/workspaces/${slug}/projects/${projectId}/custom-fields/${fieldId}/value/`,
      { value },
    );
  }
}
